// API Utility functions for Operiens Platform
// All sensitive API calls are routed through n8n webhooks (server-side)

// ─── WEBHOOK URLS (safe to expose - these are YOUR endpoints) ───────────────
const OTP_WEBHOOK = 'https://n8n.operines.me/webhook/otp-whatsapp-operines'
const OTP_EMAIL_WEBHOOK =
  'https://n8n.operines.me/webhook/otp-whatsapp-operines-mail'
const SYSTEM_MESSAGE_WEBHOOK = 'https://n8n.operines.me/webhook/operines.ai'
const INSTANCE_SETUP_WEBHOOK = 'https://n8n.operines.me/webhook/instance/setup'
const EVOLUTION_WEBHOOK = 'https://n8n.operines.me/webhook/evolution/events'
const DOLIBARR_WEBHOOK = 'https://n8n.operines.me/webhook/dolibarr'
const ODOO_WEBHOOK = 'https://n8n.operines.me/webhook/odoo'
const CHATGPT_WEBHOOK = 'https://n8n.operines.me/webhook/chatgpt'
// ─────────────────────────────────────────────────────────────────────────────

// Types
export interface EvolutionInstance {
  instanceName: string
  instanceId: string
  status: 'connecting' | 'connected' | 'disconnected'
}

export type PlanType = 'starter' | 'business' | 'pro' | 'trial'

export interface UserData {
  name: string
  email: string
  phone: string
  instanceName?: string
  systemMessage?: string
  plan?: PlanType
  trialStartDate?: string
  trialEndDate?: string
  isTrialActive?: boolean
  isAccountSuspended?: boolean
  dolibarrThirdPartyId?: string
}

export interface QRCodeResponse {
  base64?: string
  code?: string
  pairingCode?: string
  qrcode?: {
    base64?: string
    code?: string
  }
}

export interface InstanceStats {
  phone?: string
  profileName?: string
  profilePicUrl?: string
  state?: string
  messageCount?: number
}

export interface WebhookSettings {
  enabled: boolean
  url?: string
  events: string[]
}

// Evolution API Helpers (all routed through n8n webhooks)
export const evolutionAPI = {
  // WEBHOOK-BASED QR CODE GENERATION - SUPPORTS BINARY PNG RESPONSE
  getQRCode: async (instanceName: string): Promise<string | null> => {
    const token =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)

    console.log('=== WEBHOOK QR REQUEST ===')
    console.log('Instance Name:', instanceName)

    try {
      const response = await fetch(EVOLUTION_WEBHOOK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          businessName: instanceName,
          instanceName: instanceName,
          token,
          action: 'create_qr',
          connectionMethod: 'qr',
        }),
      })

      console.log('Response Status:', response.status)

      if (!response.ok) {
        console.error('❌ Request failed with status:', response.status)
        return null
      }

      const contentType = response.headers.get('content-type') || ''

      // Handle Binary Image Response (PNG/JPEG/etc)
      if (contentType.includes('image/')) {
        console.log('✅ Received binary image response')
        const blob = await response.blob()
        const objectUrl = URL.createObjectURL(blob)
        return objectUrl
      }

      // Handle JSON Response (fallback)
      if (contentType.includes('application/json')) {
        const data = await response.json()

        if (data.qr && typeof data.qr === 'string' && data.qr.length > 50) {
          if (data.qr.startsWith('data:image/')) {
            return data.qr
          }
          return `data:image/png;base64,${data.qr}`
        }

        if (
          data.code &&
          typeof data.code === 'string' &&
          data.code.length > 50
        ) {
          if (data.code.startsWith('data:image/')) {
            return data.code
          }
          return `data:image/png;base64,${data.code}`
        }

        console.error('❌ No QR code found in JSON response')
        return null
      }

      console.error('❌ Unknown Content-Type:', contentType)
      return null
    } catch (error: any) {
      console.error('❌ Error:', error.message)
      return null
    }
  },

  // WEBHOOK-BASED CONNECTION CHECK
  checkConnection: async (instanceName: string): Promise<any> => {
    try {
      const response = await fetch(EVOLUTION_WEBHOOK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          instanceName,
          action: 'check_connection',
        }),
      })

      if (!response.ok) {
        return { state: 'close' }
      }

      const data = await response.json()
      const state = data?.state || data?.instance?.state || 'close'

      return { ...data, state }
    } catch (error: any) {
      console.error('Error checking connection:', error)
      return { state: 'close' }
    }
  },

  // WEBHOOK-BASED FETCH INSTANCE
  fetchInstance: async (instanceName: string): Promise<any> => {
    try {
      const response = await fetch(EVOLUTION_WEBHOOK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          instanceName,
          action: 'fetch_instance',
        }),
      })

      if (!response.ok) {
        return null
      }

      return await response.json()
    } catch (error: any) {
      console.error('Error fetching instance:', error)
      return null
    }
  },

  // WEBHOOK-BASED GET INSTANCE STATS
  getInstanceStats: async (
    instanceName: string,
  ): Promise<InstanceStats | null> => {
    try {
      const instanceInfo = await evolutionAPI.fetchInstance(instanceName)

      if (!instanceInfo) return null

      const state =
        instanceInfo?.instance?.state || instanceInfo?.state || 'close'
      const phone = instanceInfo?.instance?.owner || instanceInfo?.owner
      const profileName =
        instanceInfo?.instance?.profileName || instanceInfo?.profileName
      const profilePicUrl =
        instanceInfo?.instance?.profilePicUrl || instanceInfo?.profilePicUrl

      return { phone, profileName, profilePicUrl, state, messageCount: 0 }
    } catch (error) {
      console.error('Error getting instance stats:', error)
      return null
    }
  },

  // DEPRECATED stubs
  createInstance: async (_instanceName: string): Promise<any> => {
    console.warn('⚠️ createInstance is deprecated - use webhook instead')
    return { success: true }
  },
  connectInstance: async (_instanceName: string): Promise<QRCodeResponse> => {
    console.warn('⚠️ connectInstance is deprecated - use webhook instead')
    return {}
  },
  getWebhookSettings: async (
    _instanceName: string,
  ): Promise<WebhookSettings | null> => null,
  updateWebhookSettings: async (
    _instanceName: string,
    _settings: Partial<WebhookSettings>,
  ): Promise<boolean> => true,
  deleteInstance: async (_instanceName: string): Promise<boolean> => true,
  logoutInstance: async (_instanceName: string): Promise<any> => ({}),
}

// Dolibarr API Integration (routed through n8n webhook - keys are server-side)
export const dolibarrAPI = {
  createThirdParty: async (userData: UserData): Promise<string | null> => {
    console.log('=== Creating Third Party via webhook ===')
    try {
      const payload = {
        action: 'create_third_party',
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
        plan: userData.plan || 'trial',
        timestamp: new Date().toISOString(),
      }

      const response = await fetch(DOLIBARR_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        try {
          const data = await response.json()
          const thirdPartyId = data.id || data
          return thirdPartyId.toString()
        } catch {
          return null
        }
      }

      // Fallback: try no-cors mode
      try {
        await fetch(DOLIBARR_WEBHOOK, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        return 'pending'
      } catch {
        return null
      }
    } catch (error: any) {
      console.error('Error creating Third Party:', error)
      return null
    }
  },

  getThirdPartyByEmail: async (email: string): Promise<any | null> => {
    try {
      const response = await fetch(DOLIBARR_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get_by_email',
          email,
        }),
      })

      if (response.ok) {
        const data = await response.json()
        if (data && (data.id || (Array.isArray(data) && data.length > 0))) {
          return Array.isArray(data) ? data[0] : data
        }
      }
      return null
    } catch (error: any) {
      console.error('Error searching Third Party:', error)
      return null
    }
  },
}

export const calculateTrialDates = () => {
  const now = new Date()
  const trialEnd = new Date(now)
  trialEnd.setDate(trialEnd.getDate() + 7)

  return {
    trialStartDate: now.toISOString(),
    trialEndDate: trialEnd.toISOString(),
    isTrialActive: true,
    isAccountSuspended: false,
  }
}

export const isTrialExpired = (trialEndDate: string): boolean => {
  const now = new Date()
  const endDate = new Date(trialEndDate)
  return now > endDate
}

export const n8nAPI = {
  setupInstance: async (instanceName: string): Promise<boolean> => {
    try {
      // Only send instance name - n8n handles keys server-side
      const payload = {
        instanceName,
        enableBot: true,
        timestamp: new Date().toISOString(),
      }

      const response = await fetch(INSTANCE_SETUP_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (response.ok || response.status === 200) {
        return true
      }

      return true
    } catch (error: any) {
      console.error('Error setting up instance with n8n:', error)

      if (
        error.message.includes('CORS') ||
        error.message.includes('Failed to fetch')
      ) {
        try {
          await fetch(INSTANCE_SETUP_WEBHOOK, {
            method: 'POST',
            mode: 'no-cors',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              instanceName,
              enableBot: true,
              timestamp: new Date().toISOString(),
            }),
          })
          return true
        } catch (noCorsError) {
          console.error('No-cors mode also failed:', noCorsError)
        }
      }

      throw error
    }
  },
}

export function generateOTP(): string {
  return Math.floor(100000 + Math.random() * 900000).toString()
}

export const googleSheets = {
  saveConfig: async (
    data: UserData & { systemMessage: string },
  ): Promise<boolean> => {
    try {
      const payload = {
        instanceName: data.instanceName,
        systemMessage: data.systemMessage,
        userData: {
          name: data.name,
          email: data.email,
          phone: data.phone,
        },
        timestamp: new Date().toISOString(),
      }

      try {
        const response = await fetch(SYSTEM_MESSAGE_WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })

        if (response.ok || response.status === 200) {
          return true
        }

        return true
      } catch (fetchError: any) {
        if (
          fetchError.message.includes('CORS') ||
          fetchError.message.includes('Failed to fetch')
        ) {
          await fetch(SYSTEM_MESSAGE_WEBHOOK, {
            method: 'POST',
            mode: 'no-cors',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
          return true
        }

        throw fetchError
      }
    } catch (error: any) {
      console.error('Error sending to webhook:', error)
      throw new Error(error.message || 'فشل في حفظ الإعدادات.')
    }
  },
}

// Odoo Integration (routed through n8n webhook - keys are server-side)
export const odoo = {
  createContact: async (data: UserData): Promise<boolean> => {
    console.log('=== Creating Contact via webhook ===')
    try {
      const payload = {
        action: 'create_contact',
        name: data.name,
        email: data.email,
        phone: data.phone,
        plan: data.plan || 'trial',
        trialStartDate: data.trialStartDate || 'N/A',
        timestamp: new Date().toISOString(),
      }

      const response = await fetch(ODOO_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        console.log('✅ Contact creation request sent')
        return true
      }

      // Fallback: try no-cors
      await fetch(ODOO_WEBHOOK, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      return true
    } catch (error: any) {
      console.error('Error creating contact:', error)
      return false
    }
  },
}

export const authAPI = {
  sendOTP: async (
    phone: string,
    userData: Partial<UserData>,
    method: 'whatsapp' | 'email' = 'whatsapp',
  ): Promise<string> => {
    const otp = generateOTP()

    try {
      const webhookUrl = method === 'email' ? OTP_EMAIL_WEBHOOK : OTP_WEBHOOK

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone,
          email: userData.email,
          otp,
          method,
          userData: {
            name: userData.name,
            email: userData.email,
            timestamp: new Date().toISOString(),
          },
        }),
      })

      if (!response.ok) {
        console.error('Failed to send OTP to webhook')
      }

      return otp
    } catch (error) {
      console.error('Error sending OTP:', error)
      return otp
    }
  },

  verifyOTP: async (
    _phone: string,
    code: string,
    expectedOTP: string,
  ): Promise<boolean> => {
    return code === expectedOTP
  },
}

// ChatGPT Integration (routed through n8n webhook - OpenAI key is server-side)
export const chatGPT = {
  sendMessage: async (
    message: string,
    language: 'ar' | 'en' = 'en',
  ): Promise<string> => {
    console.log('=== Sending message to ChatGPT via webhook ===')

    try {
      const response = await fetch(CHATGPT_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          language,
          timestamp: new Date().toISOString(),
        }),
      })

      if (response.ok) {
        const rawData = await response.json()
        // Handle both array (n8n "All Incoming Items") and object responses
        const data = Array.isArray(rawData) ? rawData[0] : rawData
        const reply =
          data?.output ||
          data?.reply ||
          data?.message ||
          data?.content ||
          data?.text ||
          ''
        if (reply) {
          console.log('✅ ChatGPT response received via webhook')
          return reply
        }
        // Fallback: try to find any string value
        if (data && typeof data === 'object') {
          const values = Object.values(data)
          const firstString = values.find(
            (v) => typeof v === 'string' && (v as string).length > 5,
          )
          if (firstString) return firstString as string
        }
      }
      // Fallback response
      return language === 'ar'
        ? 'مرحباً! أنا مساعد Operines الذكي. كيف يمكنني مساعدتك اليوم؟ 😊'
        : "Hello! I'm Operines AI assistant. How can I help you today? 😊"
    } catch (error: any) {
      console.error('Error calling ChatGPT webhook:', error)

      return language === 'ar'
        ? 'مرحباً! أنا مساعد Operines الذكي. كيف يمكنني مساعدتك اليوم؟ 😊'
        : "Hello! I'm Operines AI assistant. How can I help you today? 😊"
    }
  },
}
